import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  IconButton,
  Tooltip,
  CardHeader,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Page from '../../components/Page';
import Loader from '../../components/Loader';
import { fetchAdvertisersPerformance, fetchAgentsStats } from '../../services/company-stats';
import { AgentsStatsResponse } from '../../services/company-stats/types';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { config } from '../../utils/utils';
import { useAppSelector } from '../../store';
import CompanyKpiPdfExport from '../../components/CompanyKpiPdfExport';

// Register ChartJS components and plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

interface Advertiser {
  advertiserId: string;
  name: string;
  email: string;
  userRole: string;
  totalViews: number;
  totalContactClicks: number;
  adsPublished: number;
}

interface AdvertisersPerformanceResponse {
  advertisers: Advertiser[];
  totalAdvertisers: number;
  summary: {
    totalPublished: number;
    averagePerMonth: number;
    publishedPercentage: number;
    totalViews: number;
  };
  startDate: string;
  endDate: string;
  skip: number;
  limit: number;
}

interface AdsMonthlyStatusData {
  month: string;
  year: number;
  monthNumber: number;
  published: number;
  rejected: number;
  suspended: number;
  total: number;
  monthYear: string;
}

const CompanyAdsPerformance = () => {
  const navigate = useNavigate();
  const { accessToken, user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<AdvertisersPerformanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [adsMonthlyStatus, setAdsMonthlyStatus] = useState<AdsMonthlyStatusData[]>([]);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);
  const [agentsStats, setAgentsStats] = useState<AgentsStatsResponse | null>(null);

  // Chart refs for PDF export
  const chart1Ref = React.useRef<HTMLDivElement>(null);
  const chart2Ref = React.useRef<HTMLDivElement>(null);

  // Date range state
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  const [startDate, setStartDate] = useState<Date | null>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(lastDayOfMonth);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(firstDayOfMonth);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(lastDayOfMonth);
  const [dateError, setDateError] = useState<string | null>(null);

  // Format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Validate date range
  const validateDateRange = (start: Date | null, end: Date | null): boolean => {
    if (!start || !end) {
      setDateError('Both start and end dates are required');
      return false;
    }
    if (start > end) {
      setDateError('Start date must be before or equal to end date');
      return false;
    }
    setDateError(null);
    return true;
  };

  const handleStartDateChange = (value: Date | null) => {
    setStartDate(value);
    if (value && endDate) {
      validateDateRange(value, endDate);
    }
  };

  const handleEndDateChange = (value: Date | null) => {
    setEndDate(value);
    if (startDate && value) {
      validateDateRange(startDate, value);
    }
  };

  const handleApplyFilters = () => {
    if (validateDateRange(startDate, endDate)) {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
      setPage(0); // Reset to first page when filters change
    }
  };

  const handleResetFilters = () => {
    const newStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const newEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setAppliedStartDate(newStartDate);
    setAppliedEndDate(newEndDate);
    setDateError(null);
    setPage(0);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!appliedStartDate || !appliedEndDate) return;

      setLoading(true);
      setLoadingChart(true);
      setError(null);

      try {
        const startDateStr = formatDateToYYYYMMDD(appliedStartDate);
        const endDateStr = formatDateToYYYYMMDD(appliedEndDate);

        const [result, monthlyStatusResponse, agentsStatsResult] = await Promise.all([
          fetchAdvertisersPerformance({
            startDate: startDateStr,
            endDate: endDateStr,
            skip: page * rowsPerPage,
            limit: rowsPerPage,
          }),
          fetch(`${config.BASE_URL}/company-stats/ads-monthly-status?startDate=${startDateStr}&endDate=${endDateStr}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }),
          fetchAgentsStats({
            startDate: startDateStr,
            endDate: endDateStr,
          })
        ]);

        setData(result);
        setAgentsStats(agentsStatsResult);

        if (monthlyStatusResponse.ok) {
          const monthlyStatusData = await monthlyStatusResponse.json();
          setAdsMonthlyStatus(monthlyStatusData.data || []);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch performance data');
        console.error('Error fetching advertisers performance:', err);
      } finally {
        setLoading(false);
        setLoadingChart(false);
      }
    };

    fetchData();
  }, [appliedStartDate, appliedEndDate, page, rowsPerPage, accessToken]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (userId: string) => {
    navigate(`/company-ads-performance/details/${userId}`);
  };

  // Fetch all data for PDF export
  const fetchAllAdvertisersData = async (): Promise<AdvertisersPerformanceResponse | null> => {
    if (!appliedStartDate || !appliedEndDate) return null;
    
    try {
      const startDateStr = formatDateToYYYYMMDD(appliedStartDate);
      const endDateStr = formatDateToYYYYMMDD(appliedEndDate);
      
      const result = await fetchAdvertisersPerformance({
        startDate: startDateStr,
        endDate: endDateStr,
        skip: 0,
        limit: 10000, // Fetch all
      });
      
      return result;
    } catch (error) {
      console.error('Error fetching all advertisers data:', error);
      return null;
    }
  };

  // Calculate optimal bar width based on month count
  const getOptimalBarPercentage = () => {
    if (adsMonthlyStatus.length <= 3) return 0.5;
    if (adsMonthlyStatus.length <= 6) return 0.7;
    return 0.8;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        padding: 20,
        boxWidth: 15,
        font: {
          size: 12
        }
      },
      tooltip: {
        enabled: true,
      },
      datalabels: {
        display: function(context: any) {
          return context.dataset.data[context.dataIndex] !== null;
        },
        color: '#000000',
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 8,
        formatter: function(value: number | null) {
          return value !== null ? value.toString() : '';
        },
        font: {
          weight: 'bold' as const,
          size: 16,
        },
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: function(context: any) {
          const max = context.chart.data.datasets.reduce(
            (max: number, dataset: any) => {
              const filteredData = dataset.data.filter((value: any) => value !== null);
              const dataMax = filteredData.length > 0 ? Math.max(...filteredData) : 0;
              return dataMax > max ? dataMax : max;
            }, 0);
          return max * 1.2;
        },
        grid: {
          drawBorder: true,
          color: '#E2E2E2',
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          font: {
            size: 12
          }
        },
        grid: {
          display: false
        }
      }
    },
    skipNull: true,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 30,
        bottom: 10
      }
    }
  };

  // Prepare ads monthly status chart data
  const adsMonthlyStatusChart = {
    labels: adsMonthlyStatus.map(item => item.monthYear || `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Published',
        data: adsMonthlyStatus.map(item => item.published === 0 ? null : item.published),
        backgroundColor: '#0000FF',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Rejected',
        data: adsMonthlyStatus.map(item => item.rejected === 0 ? null : item.rejected),
        backgroundColor: '#FFA500',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Suspended',
        data: adsMonthlyStatus.map(item => item.suspended === 0 ? null : item.suspended),
        backgroundColor: '#808080',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Total',
        data: adsMonthlyStatus.map(item => item.total === 0 ? null : item.total),
        backgroundColor: '#9E0000',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
    ],
  };

  // Prepare monthly published ads chart data (only published count)
  const monthlyPublishedAdsChart = {
    labels: adsMonthlyStatus.map(item => item.monthYear || `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Published Ads',
        data: adsMonthlyStatus.map(item => item.published || 0),
        backgroundColor: '#0000FF',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
    ],
  };

  if (loading) {
    return (
      <Page title="KPI Dashboard">
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <Loader />
          </Box>
        </Container>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="KPI Dashboard">
        <Container maxWidth="xl">
          <Box sx={{ pt: 3, pb: 3 }}>
            <Typography variant="h4" color="error">Error: {error}</Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="KPI Dashboard">
      <Container maxWidth="xl">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Typography variant="h4">KPI Dashboard</Typography>
        </Box>

        {/* Date Range Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Date Filters</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth error={!!dateError} helperText={dateError} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth error={!!dateError} helperText={dateError} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<FilterListIcon />}
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <CompanyKpiPdfExport
                    data={data}
                    agentsStats={agentsStats}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    chart1Ref={chart1Ref}
                    chart2Ref={chart2Ref}
                    loading={loading}
                    formatDateToYYYYMMDD={formatDateToYYYYMMDD}
                    fetchAllData={fetchAllAdvertisersData}
                    companyName={user?.name || ''}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ad Publishing Statistics and Agent Statistics in same row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Ad Publishing Statistics Summary */}
          {data && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Ad Publishing Statistics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Total Published
                      </Typography>
                      <Typography variant="h5">{data.summary.totalPublished}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Average/Month
                      </Typography>
                      <Typography variant="h5">{data.summary.averagePerMonth}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Published %
                      </Typography>
                      <Typography variant="h5">{data.summary.publishedPercentage}%</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Total Views
                      </Typography>
                      <Typography variant="h5">{data.summary.totalViews}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Agent Statistics */}
          {agentsStats && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Agent Statistics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Total Agents
                      </Typography>
                      <Typography variant="h5">
                        {agentsStats.totalAgents || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Agent Ads
                      </Typography>
                      <Typography variant="h5">
                        {agentsStats.agents.reduce((sum, agent) => sum + agent.adsCreated, 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Monthly Published Ads Chart */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Monthly Published Ads" />
          <CardContent>
            {loadingChart ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400 }}>
                <Loader />
              </Box>
            ) : (
              <Box sx={{ height: 400 }} ref={chart1Ref}>
                <Bar data={monthlyPublishedAdsChart} options={chartOptions} />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Ads Monthly Status Chart */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Ads Monthly Status" />
          <CardContent>
            {loadingChart ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400 }}>
                <Loader />
              </Box>
            ) : (
              <Box sx={{ height: 400 }} ref={chart2Ref}>
                <Bar data={adsMonthlyStatusChart} options={chartOptions} />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Advertisers Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Statistics
            </Typography>
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell align="center">Role</TableCell>
                    <TableCell align="right">Total Views</TableCell>
                    <TableCell align="right">Contact Clicks</TableCell>
                    <TableCell align="right">Ads Published</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data && data.advertisers.length > 0 ? (
                    data.advertisers.map((advertiser, index) => (
                      <TableRow key={advertiser.advertiserId} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {advertiser.name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={advertiser.userRole === 'agent' ? 'agent' : advertiser.userRole}
                            size="small"
                            color={advertiser.userRole === 'company' ? 'primary' : 'success'}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="right">{advertiser.totalViews.toLocaleString()}</TableCell>
                        <TableCell align="right">{advertiser.totalContactClicks.toLocaleString()}</TableCell>
                        <TableCell align="right">{advertiser.adsPublished}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => handleViewDetails(advertiser.advertiserId)}
                              size="small"
                              sx={{ color: '#808080' }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No advertisers found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {data && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={data.totalAdvertisers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default CompanyAdsPerformance;

